import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import FlexBox from 'Elements/FlexBox/FlexBox';

import Grid, { Col } from 'Components/Grid';

import ClearingHeroBanner, { ClearingHeroBannerProps } from 'Campaigns/Clearing/HeroBanner';
import { ProgressLineMobile } from 'Campaigns/Clearing/ProgressLine';
import ClearingFooter, { ClearingFooterProps } from 'Campaigns/Clearing/Footer';
import CourseSearch, { CourseSearchProps } from 'Campaigns/Clearing/CourseSearch';
import SupportBlock, { ClearingSupportBlockProps } from 'Campaigns/Clearing/SupportBlock';
import { Circle } from 'Campaigns/Clearing/ProgressLine/ProgressLine.styled';
import ApplyModal, { ApplyModalProps } from 'Campaigns/Clearing/ApplyModal';

import JourneyGrades, { Props as JourneyGradesProps } from './Journeys/Grades';
import JourneyOptions, { Props as JourneyOptionsProps } from './Journeys/Options';
import FadeAnimation from './FadeAnimation';

import {
    JourneyChoiceWrapper,
    JourneyButton,
    PromiseWrapper,
    JourneyButtonProgressLine,
    JourneyButtonprogressLineHorizontalLeft,
    JourneyButtonProgressLineHorizontalRight,
    JourneyButtonProgressLineCentral
} from './Clearing.styled';

export interface Props {
    heroBanner: Omit<ClearingHeroBannerProps, 'handleApplyModal'>;
    courseSearch: CourseSearchProps;
    flow: {
        promise: ClearingSupportBlockProps;
        heading: string;
        journeyGradesLabel: string;
        journeyOptionsLabel: string;
        desc?: React.ReactNode;
    };
    journeyGrades: JourneyGradesProps;
    journeyOptions: JourneyOptionsProps;
    footer: Omit<ClearingFooterProps, 'handleApplyModal'>;
    enableApplicationModal: boolean;
    applyModal: Omit<ApplyModalProps, 'modal'>;
}

export const DEFAULT_MARGIN_TOP = '0';

export const FadeInUpAnimation: React.FC = ({ children }) => (
    <FadeAnimation transformInView="translateY(0px)" transformOutView="translateY(50px)">
        {children}
    </FadeAnimation>
);

const Clearing: React.FC<Props> = props => {
    const {
        heroBanner,
        courseSearch,
        flow,
        journeyGrades,
        journeyOptions,
        footer,
        enableApplicationModal,
        applyModal
    } = props;

    useEffect(() => {
        const link = document.createElement('link');

        link.rel = 'stylesheet';
        link.href = 'https://use.typekit.net/mul1xwk.css';

        document.body.appendChild(link);
    }, []);

    const router = useRouter();
    const { journey } = router.query;
    const [useAltJourney, setUseAltJourney] = useState(journey === 'alt');
    const [isApplyVisible, setIsApplyVisible] = useState<boolean>(false);

    useEffect(() => {
        useAltJourney
            ? router.replace({ query: { ...router.query, journey: 'alt' } }, undefined, {
                  shallow: true,
                  scroll: false
              })
            : router.replace({ query: { ...router.query, journey: 'default' } }, undefined, {
                  shallow: true,
                  scroll: false
              });
    }, [useAltJourney]);

    return (
        <Main>
            <ClearingHeroBanner
                {...heroBanner}
                handleApplyModal={
                    enableApplicationModal ? () => setIsApplyVisible(true) : undefined
                }
            />
            <Section id="solents-promise" paddingBottom={0}>
                <PromiseWrapper>
                    <Grid rowGap="2rem">
                        <Col m={[1, 8]} l={[1, 9]}>
                            <div style={{ position: 'relative' }}>
                                <SupportBlock
                                    heading={flow.promise.heading}
                                    text={flow.promise.text}
                                    decorativeLines="none"
                                    pseudoBlocks="none"
                                    clearingPromise={true}
                                />
                            </div>
                        </Col>
                        <Col m={[8, 13]} l={[9, 13]} align="center">
                            <CourseSearch {...courseSearch} />
                        </Col>
                    </Grid>
                </PromiseWrapper>
            </Section>
            <Section id="journey-choice" paddingBottom={0.16}>
                <JourneyChoiceWrapper>
                    <Grid>
                        <Col textAlign="center">
                            <h2>{flow.heading}</h2>
                            <FlexBox justifyContent="center" gap={{ base: '1rem', medium: '5rem' }}>
                                <JourneyButton
                                    ariaLabel={`Change flow to ${flow.journeyGradesLabel}`}
                                    onClick={() => setUseAltJourney(false)}
                                    $active={!useAltJourney}
                                >
                                    {flow.journeyGradesLabel}
                                    <JourneyButtonProgressLine $active={!useAltJourney} />
                                    <JourneyButtonprogressLineHorizontalLeft
                                        $active={!useAltJourney}
                                    />
                                </JourneyButton>
                                <JourneyButton
                                    ariaLabel={`Change flow to ${flow.journeyOptionsLabel}`}
                                    onClick={() => setUseAltJourney(true)}
                                    $active={useAltJourney}
                                >
                                    {flow.journeyOptionsLabel}
                                    <JourneyButtonProgressLine $active={useAltJourney} />
                                    <JourneyButtonProgressLineHorizontalRight
                                        $active={useAltJourney}
                                    />
                                </JourneyButton>
                                <Circle $active={true} style={{ bottom: '-3.25rem', zIndex: 1 }} />
                                <JourneyButtonProgressLineCentral />
                            </FlexBox>
                        </Col>
                    </Grid>
                </JourneyChoiceWrapper>
            </Section>
            {!useAltJourney ? (
                <JourneyGrades {...journeyGrades} />
            ) : (
                <JourneyOptions {...journeyOptions} />
            )}
            <ProgressLineMobile />
            <ClearingFooter
                {...footer}
                handleApplyModal={
                    enableApplicationModal ? () => setIsApplyVisible(true) : undefined
                }
            />
            {heroBanner.isClearingOpen && enableApplicationModal && (
                <ApplyModal
                    {...applyModal}
                    modal={{
                        id: 'clearing-apply',
                        isVisible: isApplyVisible,
                        closeFnc: () => setIsApplyVisible(false)
                    }}
                />
            )}
        </Main>
    );
};

export default Clearing;
